import { useEffect, useState, useMemo } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useCompany } from '@/contexts/useCompany';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { CompanySwitcher } from '@/components/company/CompanySwitcher';
import { PremiumModal } from '@/components/modals/PremiumModal';
import { SignUpModal } from '@/components/modals/SignUpModal';
import { useCreateQuote } from '@/hooks/useCreateQuote';
import { startWelcomeTour } from '@/lib/welcomeTour';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { PendingDocumentsCard } from '@/components/dashboard/PendingDocumentsCard';
import { supabase } from '@/integrations/supabase/client';
import type { Quote } from '@/types/database';
import {
  FileText,
  User,
  BarChart3,
  ClipboardList,
  Crown,
  Lightbulb,
  Palette,
  Receipt,
  ReceiptText,
  DollarSign,
  CalendarDays,
  FileSignature,
  Boxes,
  ShoppingCart,
  Workflow,
  MoreHorizontal,
  ChevronUp
} from 'lucide-react';
import { useRole } from '@/hooks/useRole';
import { useFeature } from '@/hooks/useFeature';
import { useFiscalReadiness, type FiscalTag } from '@/hooks/useFiscalReadiness';
import { FiscalTags } from '@/components/nfse/FiscalTags';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CONTENT_CARD } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { cacheDashboardData, getCachedDashboardData, getCachedQuotes } from '@/lib/offlineDataCache';
import { getPublicQuoteUrl } from '@/lib/urls';
import { SendMessageDialog } from '@/components/messages/SendMessageDialog';
import { buildQuoteMessageContext } from '@/lib/messageTemplates';
import { useRecentDocuments } from '@/hooks/useRecentDocuments';
import { usePendingDocuments } from '@/hooks/usePendingDocuments';
import { RecentDocumentsList } from '@/components/dashboard/RecentDocumentsList';
import { useFinanceEntriesRaw } from '@/hooks/useFinance';
import { computeCashBalance } from '@/lib/cashBalance';
import { formatBRLShort } from '@/lib/financeLabels';
import { plural } from '@/lib/plural';
import { MODULE_LABELS } from '@/lib/labels';
import onboardingLogo from '@/components/images/logo/logo-orcamigo-onboarding.png';

function dedupeQuotesById(list: Quote[]): Quote[] {
  const map = new Map<string, Quote>();
  for (const q of list) {
    if (!q?.id) continue;
    // Keep first occurrence (most recent) in the provided order
    if (!map.has(q.id)) map.set(q.id, q);
  }
  return Array.from(map.values());
}

function sortQuotesNewestFirst(list: Quote[]): Quote[] {
  return [...list].sort((a, b) => {
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    return tb - ta;
  });
}

const baseMenuCards = [
  {
    // Grátis: a lista de trabalhos é a leitura de fluxo sobre documentos que o
    // usuário já tem, e é ela que torna contrato e nota visíveis para quem não
    // assina — cobrar por ela fecharia a própria vitrine.
    icon: Workflow,
    label: 'Trabalhos',
    path: '/trabalhos',
    iconBg: 'bg-violet-500',
    iconColor: 'text-white'
  },
  {
    icon: ShoppingCart,
    label: MODULE_LABELS.quickSale.one,
    path: '/venda',
    iconBg: 'bg-emerald-500',
    iconColor: 'text-white'
  },
  {
    icon: ClipboardList,
    label: MODULE_LABELS.serviceOrder.many,
    path: '/ordens-servico',
    iconBg: 'bg-orange-500',
    iconColor: 'text-white'
  },
  {
    icon: CalendarDays,
    label: 'Agenda',
    path: '/agenda',
    iconBg: 'bg-sky-500',
    iconColor: 'text-white'
  },
  {
    icon: ReceiptText,
    label: 'Recibos',
    path: '/recibos',
    iconBg: 'bg-teal-500',
    iconColor: 'text-white'
  },
];

const reportsCard = {
  icon: BarChart3,
  label: 'Relatórios',
  path: '/reports',
  iconBg: 'bg-purple-500',
  iconColor: 'text-white',
} as const;

export default function DashboardPage() {
  const { user, profile } = useAuth();
  const {
    remainingQuotes,
    isPremium,
    quotesCreatedInPeriod,
    freeQuotesLimit,
  } = useSubscription();
  const { companies, activeCompany } = useCompany();
  const { isOnline } = useNetworkStatus();
  const { canSeeFinance } = useRole();
  const readiness = useFiscalReadiness();
  const contracts = useFeature('contracts');
  const inventory = useFeature('inventory');
  const reportsAdvanced = useFeature('reports_advanced');
  const navigate = useNavigate();

  // Menu shortcuts. Nota Fiscal e Financeiro só para quem pode ver finanças
  // (owner/admin com conta ativa), espelhando as regras da bottom nav / páginas.
  const menuCards = useMemo(() => {
    const cards: Array<{
      icon: typeof FileText;
      label: string;
      path: string;
      iconBg: string;
      iconColor: string;
      pro?: boolean;
      tags?: FiscalTag[];
      /**
       * Fica atrás do "Mais". A régua é FREQUÊNCIA DE USO, não importância: são
       * os módulos que o prestador abre esporadicamente (fechar um contrato,
       * conferir o estoque, olhar relatório). Deixá-los na primeira dobra fazia
       * a home abrir com 10 ícones — a grade virava um painel e a ação que a
       * pessoa veio fazer (orçar) não aparecia em lugar nenhum.
       *
       * Não é esconder: um toque abre, e os selos PRO continuam visíveis quando
       * abre, então o upsell não se perde.
       */
      secondary?: boolean;
    }> = [...baseMenuCards];

    if (user && canSeeFinance) {
      cards.push({
        icon: Receipt,
        label: MODULE_LABELS.invoice.one,
        path: '/nfse',
        iconBg: 'bg-indigo-500',
        iconColor: 'text-white',
        // Entry-point sempre visível; badges PRO/CNPJ (via readiness) sinalizam
        // o que falta. Clique segue para /nfse, onde os gates explicam/oferecem
        // upgrade (free) ou Recibo (autônomo). Ver useFiscalReadiness.
        // Durante o load do perfil não mostra tag: o perfil ainda null renderiza
        // 'CNPJ' — sinal errado p/ um MEI legítimo enquanto a query resolve.
        tags: readiness.isLoading ? [] : readiness.tags,
      });
      cards.push({
        icon: DollarSign,
        label: 'Financeiro',
        path: '/financeiro',
        iconBg: 'bg-green-500',
        iconColor: 'text-white',
      });
    }

    // Contratos: recurso PRO, disponível a qualquer usuário logado (não é finanças).
    if (user) {
      cards.push({
        icon: FileSignature,
        label: 'Contratos',
        path: '/contratos',
        iconBg: 'bg-violet-500',
        iconColor: 'text-white',
        pro: contracts.upsell,
        secondary: true,
      });
      // Estoque: recurso PRO, disponível a qualquer usuário logado (não é finanças).
      cards.push({
        icon: Boxes,
        label: 'Estoque',
        path: '/estoque',
        iconBg: 'bg-amber-500',
        iconColor: 'text-white',
        pro: inventory.upsell,
        secondary: true,
      });
    }

    cards.push({ ...reportsCard, pro: reportsAdvanced.upsell, secondary: true });
    return cards;
  }, [user, canSeeFinance, readiness.tags, readiness.isLoading, contracts.upsell, inventory.upsell, reportsAdvanced.upsell]);
  /** Módulos de uso esporádico ficam atrás do "Mais" (ver `secondary` acima). */
  const [showAllModules, setShowAllModules] = useState(false);
  const secondaryMenuCards = useMemo(() => menuCards.filter((c) => c.secondary), [menuCards]);
  const visibleMenuCards = useMemo(
    () => (showAllModules ? menuCards : menuCards.filter((c) => !c.secondary)),
    [menuCards, showAllModules],
  );
  const { signUpOpen, setSignUpOpen } = useCreateQuote();
  const [searchParams, setSearchParams] = useSearchParams();

  const [allQuotes, setAllQuotes] = useState<Quote[]>([]);
  /** Orçamento cujo envio por mensagem está aberto (um diálogo para a lista toda). */
  const [messageQuote, setMessageQuote] = useState<Quote | null>(null);
  const [guestQuote, setGuestQuote] = useState<{
    id: string;
    client_name: string;
    total: number;
    status: string;
    created_at: string;
  } | null>(null);
  const [stats, setStats] = useState({
    totalApproved: 0,
    totalPending: 0,
    totalCompleted: 0,
    totalCancelled: 0,
  });
  const [loading, setLoading] = useState(true);
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const FIRST_LOGIN_KEY = 'orcazap_first_login_pending';
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'true') {
      toast.success('🎉 Parabéns! Sua assinatura PRO foi ativada com sucesso!', {
        duration: 6000,
        // Mantém a barra de progresso do toast em sincronia com a duração custom.
        style: { '--toast-duration': '6s' } as React.CSSProperties,
        description: 'Aproveite todos os recursos exclusivos do Orçamigo PRO.',
      });
      // Clean up the URL
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const GUEST_QUOTE_KEY = 'orcazap_guest_quote';

  const fetchData = async (userId: string) => {
    if (!activeCompany) return;
    // Don't show loading if we already have cached data
    const cached = getCachedDashboardData(userId);
    if (!cached) {
      setLoading(true);
    }

    try {
      /**
       * UMA consulta a `quotes`, e ela serve os DOIS consumidores da página: o
       * "A receber" do hero e a parte de orçamentos/vendas dos "Últimos
       * documentos".
       *
       * Antes eram duas — a de estatísticas e uma `select('*')` com
       * `.limit(5)` e `.neq('document_type', 'pedido')` para a lista de
       * recentes. A segunda saiu por dois motivos:
       *
       * 1. a lista deixou de ser só de orçamentos, então a venda de balcão
       *    passou a PERTENCER a ela (e o `.neq()` a excluía de propósito);
       * 2. aquele `.neq()` carregava a armadilha #82-b viva: em SQL
       *    `NULL <> 'pedido'` é NULL, não `true`, então **todo orçamento
       *    legado sem `document_type` estava sumindo da lista de recentes** —
       *    sem erro, sem log, sem ninguém notar. Com a partição feita no
       *    cliente por predicado POSITIVO (`isSaleDocument`), o legado volta a
       *    contar como orçamento, que é o que ele é.
       *
       * `document_type` continua OBRIGATÓRIO na projeção: sem ele a partição do
       * PDV (buildDashboardStats) não reconhece a venda de balcão e o número do
       * hero volta a somá-la (#82-a2). `title`/`sequence_id` entraram para a
       * lista poder mostrar o documento sem uma segunda leitura.
       */
      const { data: allQuotesData, error: allQuotesError } = await supabase
        .from('quotes')
        .select('id, total, status, created_at, client_name, client_phone, document_type, title, sequence_id')
        .eq('company_id', activeCompany.id);

      if (allQuotesError) throw allQuotesError;

      if (allQuotesData) {
        // Merge with any offline-pending quotes
        const cachedQuotes = getCachedQuotes(userId) || [];
        const offlinePending = cachedQuotes.filter((q: any) =>
          q._offline_pending === true &&
          String(q.id).startsWith('offline_')
        );
        const mergedAll = [...allQuotesData, ...offlinePending];
        const dedupedAll = dedupeQuotesById(sortQuotesNewestFirst(mergedAll as Quote[]));

        setAllQuotes(dedupedAll);

        // Cache for offline access - include merged lists containing offline-pending quotes
        cacheDashboardData(userId, {
          quotes: dedupedAll.slice(0, 5),
          allQuotes: dedupedAll
        });
      }
    } catch (error) {
      console.error('[DashboardPage] Error fetching data:', error);
      // On error, prefer the full quotes cache (contains offline-created quotes)
      const cachedQuotes = getCachedQuotes(userId) as Quote[] | null;
      if (cachedQuotes && cachedQuotes.length > 0) {
        setAllQuotes(dedupeQuotesById(sortQuotesNewestFirst(cachedQuotes)));
      } else if (cached?.allQuotes && cached.allQuotes.length > 0) {
        console.log('[DashboardPage] Using cached dashboard data after fetch error');
        setAllQuotes(dedupeQuotesById(sortQuotesNewestFirst(cached.allQuotes as Quote[])));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * O NÚMERO AO VIVO DO HERO: o SALDO — quanto dinheiro já entrou de verdade.
   *
   * O desenho oficial (`/quotes/new`) coloca no cabeçalho o valor que está
   * sendo montado; numa tela de cadastro isso virou o nome ao vivo (#160). Na
   * home ele é o resultado do prestador, e a régua é o CAIXA REALIZADO:
   * recebido − pago sobre os lançamentos já quitados (`computeCashBalance`).
   *
   * Antes o hero mostrava "A receber" — o que ainda está na mão do cliente. É
   * uma promessa, não um resultado, e um número no hero é uma AFIRMAÇÃO: o que
   * o prestador quer saber ao abrir o app é quanto ele já conseguiu. O que
   * ainda está em aberto continua visível, uma seção abaixo, no card de
   * Pendências — que é onde ele tem o que fazer a respeito.
   *
   * ⚠️ **Gate de PAPEL, não de plano (#38).** A RLS de `finance_entries` é
   * owner/admin: para um `member` a consulta devolve zero linhas, e um
   * "R$ 0,00" gigante afirmaria que ele não faturou nada. `null` faz a tela
   * ESCONDER o número — nunca inventar um.
   */
  const { entries: financeRows } = useFinanceEntriesRaw();
  const financeEntries = canSeeFinance ? financeRows : null;
  const saldo = useMemo(() => computeCashBalance(financeEntries), [financeEntries]);

  /**
   * "Últimos documentos": os orçamentos/vendas que a página já tem, fundidos
   * com ordens de serviço, contratos, recibos e notas.
   *
   * Os orçamentos entram por PARÂMETRO justamente para não haver uma segunda
   * leitura de `quotes` nesta tela — o hero e a lista falam do mesmo conjunto,
   * sempre.
   */
  const { documents, isLoading: documentsLoading } = useRecentDocuments(allQuotes);

  /**
   * PENDÊNCIAS — o que está em aberto, agrupado por tipo de documento.
   *
   * Substituiu o alerta que contava só orçamentos parados há mais de 3 dias:
   * uma OS nunca concluída, um contrato mandado e não assinado e uma conta
   * vencida não apareciam em lugar nenhum da home, e o prestador só descobria
   * abrindo módulo por módulo. O prazo de 3 dias não se perdeu — virou o selo
   * "N parados" da linha de orçamentos.
   *
   * Orçamentos e financeiro entram por PARÂMETRO: são os dois conjuntos que
   * esta página já carregou, e relê-los abriria a janela em que o card conta um
   * documento que o resto da tela ainda não conhece.
   */
  const { summary: pendings } = usePendingDocuments(allQuotes, financeEntries);

  // Check onboarding status from database (only once per session)
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user || !profile || onboardingChecked) return;

      setOnboardingChecked(true);

      // Check if user has seen onboarding in the database
      const hasSeenOnboarding = (profile as any).has_seen_onboarding === true;

      // Check if this is a new signup that just confirmed email
      // We use localStorage to track if this is a fresh signup session
      const isFirstLoginSession = localStorage.getItem(FIRST_LOGIN_KEY) === 'true';

      if (!hasSeenOnboarding && isFirstLoginSession) {
        // Clear the first login flag
        localStorage.removeItem(FIRST_LOGIN_KEY);
        // Inicia o tour de boas-vindas (invite → Ajustes → OnboardingModal).
        // O WelcomeTour (global, App.tsx) conduz e abre o OnboardingModal ao fim.
        startWelcomeTour();
      }
    };

    checkOnboarding();
  }, [user, profile, onboardingChecked]);

  // Load cached data immediately on mount (critical for offline app restart)
  useEffect(() => {
    if (!user) {
      // Only read from localStorage when NOT logged in (visitor mode)
      const storedQuote = localStorage.getItem(GUEST_QUOTE_KEY);
      if (storedQuote) {
        try {
          const parsed = JSON.parse(storedQuote);
          setGuestQuote({
            id: parsed.id,
            client_name: parsed.client_name,
            total: parsed.total,
            status: parsed.status,
            created_at: parsed.created_at,
          });
        } catch (e) {
          console.error('Error parsing guest quote:', e);
        }
      } else {
        setGuestQuote(null);
      }
      setLoading(false);
      return;
    }

    // When user is logged in, clear guest quote local state.
    // IMPORTANT: do NOT clear GUEST_QUOTE_KEY here — <GuestQuoteImportPrompt/>
    // reads it after login to offer importing the visitor's saved quote into the
    // account, and removes it from localStorage once the user chooses.
    setGuestQuote(null);

    // Prefer the full quotes cache for offline correctness (includes offline-created quotes)
    const cachedQuotes = getCachedQuotes(user.id) as Quote[] | null;
    if (cachedQuotes && cachedQuotes.length > 0) {
      setAllQuotes(dedupeQuotesById(sortQuotesNewestFirst(cachedQuotes)));
    } else {
      // Fallback: dashboard-specific cache (server snapshot)
      const cached = getCachedDashboardData(user.id);
      if (cached?.allQuotes && cached.allQuotes.length > 0) {
        setAllQuotes(dedupeQuotesById(sortQuotesNewestFirst(cached.allQuotes as Quote[])));
      }
    }
    setLoading(false);
  }, [user]);

  // Fetch from server when online (re-runs on company switch)
  useEffect(() => {
    if (!user || !isOnline || !activeCompany) return;
    fetchData(user.id);
  }, [user, isOnline, activeCompany?.id]);

  useEffect(() => {
    if (!user) return;

    const handleQuotesUpdated = () => {
      if (isOnline) {
        fetchData(user.id);
      } else {
        // Reload from full quotes cache (includes offline-created quotes)
        const cachedQuotes = getCachedQuotes(user.id) as Quote[] | null;
        if (cachedQuotes && cachedQuotes.length > 0) {
          setAllQuotes(dedupeQuotesById(sortQuotesNewestFirst(cachedQuotes)));
          return;
        }

        // Fallback: dashboard cache
        const cached = getCachedDashboardData(user.id);
        if (cached?.allQuotes) {
          setAllQuotes(dedupeQuotesById(sortQuotesNewestFirst(cached.allQuotes as Quote[])));
        }
      }
    };

    window.addEventListener('quotes-updated', handleQuotesUpdated);
    return () => window.removeEventListener('quotes-updated', handleQuotesUpdated);
  }, [user, isOnline]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  /*
   * `getStatusBadge` saiu daqui: ele conhecia só os 5 status de ORÇAMENTO, e a
   * lista agora mistura seis tipos, cada um com vocabulário próprio (contrato
   * tem "Parcial", recibo tem "Emitido", NFS-e tem doze). Quem rotula agora é
   * `timelineStatusLabel`, que já era a autoridade no Resumo do Cliente; o TOM
   * (cor) mora no `RecentDocumentsList`.
   */

  /**
   * A data de hoje no eyebrow do hero ("Segunda-feira, 4 de agosto").
   *
   * O eyebrow do desenho oficial diz ONDE você está ("Novo documento", "Novo
   * cadastro"). Na home não há "onde" — você está no começo —, então ele diz
   * QUANDO, que é o contexto que falta a quem abre o app para ver o que tem
   * para hoje. Congelado por render: um app aberto por horas não precisa virar
   * o dia sozinho, e recalcular a cada render seria trabalho à toa.
   */
  const hoje = useMemo(() => {
    const texto = format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR });
    return texto.charAt(0).toUpperCase() + texto.slice(1);
  }, []);

  // Get user's first name for greeting
  const getUserFirstName = () => {
    if (!user) return '';
    // First try to get from profile
    if (profile?.full_name) {
      const firstName = profile.full_name.split(' ')[0];
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }
    // Fallback to user metadata
    const metadata = user.user_metadata as { full_name?: string } | undefined;
    if (metadata?.full_name) {
      const firstName = metadata.full_name.split(' ')[0];
      return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    }
    // Final fallback - return "Profissional"
    return 'Profissional';
  };

  return (
    <MainLayout>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app (o mesmo de /quotes/new)            */}
        {/* ------------------------------------------------------------------ */}
        {/* O hero oficial carrega um VALOR AO VIVO: no orçamento é o total, num
            cadastro virou o nome (#160). Aqui ele é "A receber" — o único dos
            cinco números que a home mostrava que responde "o que eu faço
            agora?", e que por isso senta colado no alerta de cobrança logo
            abaixo. Saudação, notificações e perfil sobem para cá: o cabeçalho
            solto que existia antes não era desenho nenhum, era sobra. */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">
                {user ? hoje : 'Modo visitante'}
              </p>
              {/* `line-clamp-2`, não `truncate`: nome é texto livre e o do
                  usuário é a única coisa da tela que ele reconhece de longe
                  (mesma razão do #160). */}
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">
                {user ? `Olá, ${getUserFirstName()}` : 'Orçamigo'}
              </h1>
            </div>
            {import.meta.env.DEV && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/test-dashboard')}
                className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
                title="Página de Teste de Design (Dev Only)"
                aria-label="Testar Design"
              >
                <Palette className="h-4 w-4" />
              </Button>
            )}
            {user ? (
              <>
                {/* O sino vem de dentro do `NotificationCenter`, que desenha um
                    Button `ghost` — escuro sobre o verde. O estilo do hero é
                    aplicado por variante de filho para não ter que abrir o
                    componente só por causa de uma cor. */}
                <div className="shrink-0 [&>button]:h-9 [&>button]:w-9 [&>button]:rounded-full [&>button]:bg-white/15 [&>button]:text-white [&>button]:hover:bg-white/25 [&>button]:hover:text-white">
                  <NotificationCenter />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/settings')}
                  className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
                  data-tour="tour-settings-icon"
                  aria-label="Ajustes"
                >
                  <User className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button3D size="sm" color="light" className="shrink-0" onClick={() => navigate('/auth')}>
                Entrar
              </Button3D>
            )}
          </div>

          {/* O NÚMERO AO VIVO. Só para quem tem sessão E pode ler o financeiro:
              para o visitante não existe saldo, e para um `member` a RLS devolve
              zero linhas — um R$ 0,00 gigante afirmaria que ele não faturou
              nada (#38). Em ambos os casos o hero fica só com a saudação. */}
          {user && saldo && (
            <div className="relative mt-5 min-w-0">
              <p className="text-xs text-white/75">Saldo</p>
              <p
                className={cn(
                  'truncate font-bold leading-tight',
                  // MEDIDO a 360px por réplica de DOM: aqui o valor tem a
                  // largura INTEIRA do hero (328px), e não os 192px de
                  // /quotes/new — onde ele divide a linha com a coluna do
                  // número. Ou seja, nenhum dos degraus chega a ser necessário
                  // nesta tela: até "R$ 12.845.000,00" caberia em `text-3xl`.
                  //
                  // Reusa `heroTotalSizeClass` mesmo assim, e de propósito: a
                  // função é a FONTE ÚNICA da regra (#159-b) e errar para o
                  // lado conservador nunca corta o número — enquanto uma regra
                  // própria aqui seria a quarta cópia de uma medida que já
                  // divergiu entre telas uma vez.
                  heroTotalSizeClass(formatCurrency(saldo.balance)),
                )}
              >
                {formatCurrency(saldo.balance)}
              </p>
              <p className="mt-0.5 text-xs text-white/70">
                {saldo.settledCount === 0
                  ? 'Nenhum recebimento registrado ainda'
                  : `Recebido ${formatBRLShort(saldo.received)} · Pago ${formatBRLShort(saldo.paid)}`}
              </p>
            </div>
          )}
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes/new. */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* Company Switcher — full-width row, matches recent-quotes card width */}
          {user && <CompanySwitcher />}

          {/* ================================================================ */}
          {/* ATALHOS — a grade de módulos                                     */}
          {/* ---------------------------------------------------------------- */}
          {/* O DESENHO DOS ATALHOS É INTOCADO de propósito: quadrado colorido
              `rounded-2xl` + rótulo, com o "Mais" fechando a grade em 2 linhas.
              O que a seção ganhou foi um NOME — sem ele, onze ícones soltos
              abaixo de um cartão de ação não se anunciam como navegação. */}
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">Atalhos</h2>
            {/* No desktop os mesmos 4 ladrilhos ficariam com ~400px cada — um
                quadrado colorido do tamanho de um cartão. Mais colunas mantêm o
                ladrilho no tamanho de um ícone e a grade em uma linha. */}
            <div className="grid grid-cols-4 gap-2 lg:grid-cols-8 2xl:grid-cols-12">
              {visibleMenuCards.map((card) => (
                <button
                  key={card.path}
                  type="button"
                  className="flex min-w-0 flex-col items-center gap-2 rounded-lg py-2 text-center transition-transform hover:scale-105 active:scale-95"
                  // Todos os módulos do menu exigem login. Para visitante, um clique leva
                  // direto ao /auth (ação consistente) em vez de abrir a página do módulo
                  // só para bater num muro "Entrar".
                  onClick={() => navigate(user ? card.path : '/auth')}
                >
                  <div className={`relative flex h-14 w-14 items-center justify-center rounded-2xl ${card.iconBg} shadow-lg`}>
                    <card.icon className={`h-7 w-7 ${card.iconColor}`} />
                    {card.tags && card.tags.length ? (
                      <FiscalTags tags={card.tags} className="absolute -right-1 -top-2" />
                    ) : card.pro ? (
                      // Mesmo componente/estilo das tags fiscais → todos os selos de
                      // módulo ficam idênticos (unificados).
                      <FiscalTags tags={['PRO']} className="absolute -right-1 -top-2" />
                    ) : null}
                  </div>
                  <span className="text-xs font-semibold leading-tight text-foreground">{card.label}</span>
                </button>
              ))}

              {/* "Mais"/"Menos" — ocupa a última célula da grade, então o conjunto
                  fecha em 2 linhas cheias em vez das 3 linhas irregulares de antes.
                  Expande NO LUGAR: nada fica a mais de um toque. */}
              {secondaryMenuCards.length > 0 && (
                <button
                  type="button"
                  className="flex min-w-0 flex-col items-center gap-2 rounded-lg py-2 text-center transition-transform hover:scale-105 active:scale-95"
                  onClick={() => setShowAllModules((v) => !v)}
                  aria-expanded={showAllModules}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-500 shadow-lg">
                    {showAllModules ? (
                      <ChevronUp className="h-7 w-7 text-white" />
                    ) : (
                      <MoreHorizontal className="h-7 w-7 text-white" />
                    )}
                  </div>
                  <span className="text-xs font-semibold leading-tight text-foreground">
                    {showAllModules ? 'Menos' : 'Mais'}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/*
            PENDÊNCIAS — logo abaixo dos atalhos, e em cartão PRÓPRIO.

            A posição não é arbitrária: o card é a resposta a "o que eu faço
            agora?", e ela só faz sentido depois de a pessoa ter visto por onde
            fazer. Cada linha é um atalho com contagem — ela leva ao MESMO
            módulo da grade acima, já recortado no que está em aberto.

            O card não aparece quando não há pendência nenhuma (ver
            `PendingDocumentsCard`).
          */}
          {user && <PendingDocumentsCard summary={pendings} />}

          {/*
            VISITANTE — a marca vive aqui, não no hero.

            O logo é verde vivo sobre transparente: no gradiente do hero ele
            praticamente some. Num cartão claro ele continua legível, e o hero
            fica com o texto, que tem contraste garantido.
          */}
          {!user && (
            <div className={cn(CONTENT_CARD, 'flex items-center gap-4 border-primary/20 bg-primary/5 p-4')}>
              <img
                src={onboardingLogo}
                alt="Orçamigo"
                className="h-8 w-auto shrink-0 bg-transparent object-contain"
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium">Modo visitante</p>
                <p className="text-sm text-muted-foreground">
                  Você pode criar 1 orçamento sem conta
                </p>
              </div>
            </div>
          )}

          {/*
            ÚLTIMOS DOCUMENTOS — não só orçamentos.

            A seção lia uma tabela só e se chamava "Últimos orçamentos"; agora
            mistura os seis tipos que o app gera. O "Ver todos" passou a apontar
            para /trabalhos, que é a única tela do app que enxerga documento de
            qualquer tipo — mandar para /quotes seria oferecer "ver todos" e
            mostrar um recorte.
          */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Últimos documentos</h2>
              {user && documents.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => navigate('/trabalhos')}>
                  Ver todos
                </Button>
              )}
            </div>

            {!user && guestQuote ? (
              <button
                type="button"
                className={cn(CONTENT_CARD, 'flex w-full items-center justify-between p-4 text-left transition-transform active:scale-[0.99]')}
                onClick={() => navigate('/quotes/new?view=true')}
              >
                <div className="min-w-0 space-y-1">
                  <p className="truncate font-medium">{guestQuote.client_name || 'Sem cliente'}</p>
                  <p className="text-sm text-muted-foreground">
                    {format(new Date(guestQuote.created_at), "dd 'de' MMM", { locale: ptBR })}
                  </p>
                </div>
                <div className="shrink-0 space-y-1 text-right">
                  <p className="font-semibold">{formatCurrency(guestQuote.total)}</p>
                  <Badge variant="secondary" className="bg-muted text-muted-foreground">
                    Salvo localmente
                  </Badge>
                </div>
              </button>
            ) : (
              <RecentDocumentsList
                entries={documents}
                loading={loading || (Boolean(user) && documentsLoading)}
                // Só orçamento e venda: o diálogo monta o texto com
                // `buildQuoteMessageContext`, que fala de orçamento.
                onSendWhatsApp={(entry) => {
                  if (entry.kind === 'quote' || entry.kind === 'sale') {
                    setMessageQuote(entry.ref as Quote);
                  }
                }}
                emptyAction={
                  user ? (
                    <Button3D className="mt-4" onClick={() => navigate('/quotes/new')}>
                      Criar primeiro orçamento
                    </Button3D>
                  ) : undefined
                }
              />
            )}
          </div>

          {/*
            UPSELL POR ÚLTIMO — de propósito.

            O banner roxo saturado era o elemento mais barulhento da página e
            ficava ENTRE o alerta de cobrança e a lista de trabalho: ganhava
            visualmente do próprio motivo de a pessoa ter aberto o app. Aqui
            embaixo ele continua inteiro (barra de cota inclusive) e para de
            disputar atenção com o trabalho.
          */}
          {user && (
            isPremium ? (
              <Card className="overflow-hidden border-0 bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg">
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                    <Lightbulb className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">Dica PRO 💡</p>
                    <p className="text-sm text-white/80">
                      Mantenha seu catálogo atualizado para orçar mais rápido!
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="overflow-hidden border-0 bg-gradient-to-r from-purple-600 via-violet-600 to-purple-700 text-white shadow-lg">
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">
                        {remainingQuotes === 0
                          ? 'Limite deste mês atingido!'
                          : `${plural(remainingQuotes, 'orçamento', 'orçamentos')} ${remainingQuotes !== 1 ? 'restantes' : 'restante'} neste mês`}
                      </p>
                      <p className="text-sm text-white/80">
                        Renova todo dia 1º. Seja PRO e desbloqueie tudo!
                      </p>
                    </div>
                    <Button3D
                      color="amber"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setPremiumModalOpen(true)}
                    >
                      <Crown className="mr-1 h-4 w-4" />
                      PRO
                    </Button3D>
                  </div>
                  {/* Progress Bar - Quota usage */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-white/80">Orçamentos usados</span>
                      <span className="font-medium">{quotesCreatedInPeriod}/{freeQuotesLimit}</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-white/20">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${quotesCreatedInPeriod >= freeQuotesLimit
                          ? 'bg-red-400'
                          : quotesCreatedInPeriod >= freeQuotesLimit * 0.8
                            ? 'bg-amber-400'
                            : 'bg-white'
                          }`}
                        style={{ width: `${Math.min(100, (quotesCreatedInPeriod / freeQuotesLimit) * 100)}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          )}
        </div>
      </div>

      {/* Visitante que já usou o orçamento grátis: o CTA acima cai aqui. */}
      <SignUpModal open={signUpOpen} onOpenChange={setSignUpOpen} />

      {/* Premium Modal */}
      <PremiumModal
        open={premiumModalOpen}
        onOpenChange={setPremiumModalOpen}
        reason="limit"
      />

      {/* Envio do orçamento pelo card (modelo + variáveis + revisão) */}
      <SendMessageDialog
        open={messageQuote !== null}
        onOpenChange={(open) => !open && setMessageQuote(null)}
        category="quote"
        context={buildQuoteMessageContext({
          clientName: (messageQuote as any)?.client_name,
          sequenceId: (messageQuote as any)?.sequence_id,
          total: messageQuote?.total ?? null,
          // A data do orçamento é `created_at` — não existe coluna `quote_date`.
          issuedAt: messageQuote?.created_at,
          validityDays: (messageQuote as any)?.validity_days,
          quoteId: messageQuote?.id,
          buildLink: getPublicQuoteUrl,
          companyName: activeCompany?.name,
          title: (messageQuote as any)?.title,
        })}
        phone={(messageQuote as any)?.client_phone ?? ''}
        title="Enviar orçamento"
      />

    </MainLayout>
  );
}
